/**
 * EntryTemplate — Sprint 28 "Entry Template Ingest & Route"
 *
 * Multi-contest one-click DK push:
 *   1. User exports Entry template from DraftKings (per-contest CSV)
 *   2. Drops it here; backend classifies contests → WarPaint presets
 *   3. One click: auto-runs Portfolio Split with correct counts per contest
 *   4. Downloads merged CSV keyed by original Entry IDs — DK routes each
 *      lineup to the correct contest slot automatically.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  UploadSimple, FileArrowUp, Target, Lightning, DownloadSimple,
  Trash, CheckCircle, WarningCircle, Cpu,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function EntryTemplate() {
  const [template, setTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [touchCount, setTouchCount] = useState(0);
  // Per-contest lineup-count override. Keyed by contest_id → integer. Empty/undefined
  // means "use the contest's actual entry_count" (default: fill every reserved slot).
  const [countOverrides, setCountOverrides] = useState({});
  const fileRef = useRef(null);

  useEffect(() => {
    api.entryTemplateGet()
      .then((r) => setTemplate(r?.template || null))
      .catch(() => {});
    // Reuse the same player_overrides pattern PortfolioSplit uses
    api.getLineups().then((d) => {
      const o = (d?.config?.player_overrides) || {};
      setOverrides(o);
      const n = Object.values(o).reduce((acc, v) => {
        if (!v || typeof v !== "object") return acc;
        if (v.lock === true) acc += 1;
        if (v.exclude === true) acc += 1;
        if (v.boost_pct != null && Number(v.boost_pct) !== 0) acc += 1;
        if (v.max_exposure_pct != null && v.max_exposure_pct !== "") acc += 1;
        return acc;
      }, 0);
      setTouchCount(n);
    }).catch(() => {});
  }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.entryTemplateUpload(file);
      setTemplate({
        uploaded_at: res.uploaded_at,
        total_entries: res.total_entries,
        contests: res.contests,
        filename: file.name,
      });
      setLastResult(null);
      toast.success(`${res.total_entries} entries across ${res.contests.length} contests loaded`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onOptimize = async () => {
    // Backend enforces DK's Two-Touch rule (compliance.MIN_TOUCHES = 2).
    // Match server truth exactly so the toast doesn't lie.
    const MIN_TOUCHES = 2;
    if (touchCount < MIN_TOUCHES) {
      toast.error(`Two-Touch rule: set at least ${MIN_TOUCHES} player touches (locks, excludes, boosts, or exposure caps) on the Optimizer first (have ${touchCount}).`);
      return;
    }
    // Coerce override map to only include ints that differ from entry_count.
    const per_contest_num_lineups = {};
    (template?.contests || []).forEach((c) => {
      const raw = countOverrides[c.contest_id];
      if (raw === undefined || raw === "") return;
      const n = parseInt(raw, 10);
      if (!Number.isFinite(n) || n < 0) return;
      per_contest_num_lineups[c.contest_id] = n;
    });
    setOptimizing(true);
    try {
      const payload = { player_overrides: overrides };
      if (Object.keys(per_contest_num_lineups).length) {
        payload.per_contest_num_lineups = per_contest_num_lineups;
      }
      const res = await api.entryTemplateOptimize(payload);
      setLastResult(res);
      const total = res.total_lineups ?? res.batches.reduce((s, b) => s + (b.generated || 0), 0);
      toast.success(`Optimized ${res.contests_optimized} contests · ${total} unique lineups generated`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message || err?.message || "Optimize failed";
      toast.error(msg);
    } finally {
      setOptimizing(false);
    }
  };

  const onClear = async () => {
    if (!confirm("Clear the loaded Entry template? Optimized lineups will remain.")) return;
    try {
      await api.entryTemplateDelete();
      setTemplate(null);
      setLastResult(null);
      toast.success("Entry template cleared");
    } catch {
      toast.error("Clear failed");
    }
  };

  return (
    <section
      data-testid="entry-template-section"
      className="relative overflow-hidden border border-[#39FF14]/25 bg-gradient-to-br from-[#0a1a10] via-[#0b0b0b] to-[#0a0a0a]"
      style={{ boxShadow: "0 0 40px rgba(57,255,20,0.06), inset 0 1px 0 rgba(255,255,255,0.03)" }}
    >
      <div className="absolute inset-0 pointer-events-none opacity-40"
           style={{ background: "radial-gradient(700px 160px at 15% 0%, rgba(57,255,20,0.14), transparent 70%)" }} />
      <div className="relative px-5 py-5">
        <Header hasTemplate={!!template} />

        {!template && (
          <UploadPrompt
            fileRef={fileRef}
            uploading={uploading}
            onUpload={onUpload}
          />
        )}

        {template && (
          <>
            <TemplateSummary template={template} countOverrides={countOverrides} onClear={onClear} />
            <ContestTable
              contests={template.contests}
              countOverrides={countOverrides}
              onOverrideChange={(cid, val) => setCountOverrides((c) => ({ ...c, [cid]: val }))}
              lastResult={lastResult}
            />
            <ActionRow
              onOptimize={onOptimize}
              optimizing={optimizing}
              hasResult={!!lastResult}
            />
            {lastResult && <ResultsBanner result={lastResult} />}
          </>
        )}
      </div>
    </section>
  );
}

function Header({ hasTemplate }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
      <div className="md:col-span-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-2 px-2 py-1 border border-[#39FF14]/40 bg-black/40">
            <Cpu size={12} weight="fill" className="text-[#39FF14]" />
            <span className="mono text-[9px] uppercase tracking-[0.28em] text-[#39FF14] font-bold">Multi-Contest · Auto-Route</span>
          </div>
          {hasTemplate && (
            <span className="mono text-[9px] text-[#39FF14] uppercase tracking-widest">
              <CheckCircle size={10} weight="fill" className="inline mr-1" />
              template loaded
            </span>
          )}
        </div>
        <h3 className="heading text-white font-black text-2xl mt-3 leading-tight">
          One click. All your contests.
        </h3>
        <p className="text-sm text-zinc-300 max-w-3xl mt-2 leading-relaxed">
          Drop your DraftKings <span className="text-white">Export Entries</span> CSV.
          WarPaint reads every reserved slot and generates
          <span className="text-[#39FF14]"> guaranteed-unique</span> lineups across every
          contest in one shot — 2 × 150-Milly + 2 × 20-Max = 340 unique lineups, not the same
          150 repeated. Want to fill only 38 slots of a 150-Milly? Type <span className="mono text-[#39FF14]">38</span> in
          the # Lineups column. Zero manual mapping — DK routes every row to its correct
          contest via the preserved Entry IDs.
        </p>
      </div>
      {/* Auto-Route flow diagram: CSV → engine → contest fan-out. Visual
          companion to the copy that shows non-DFS visitors what the
          "one click" actually does. */}
      <div className="md:col-span-2 hidden md:block">
        <svg viewBox="0 0 320 200" className="w-full" preserveAspectRatio="xMidYMid meet">
          <defs>
            <marker id="arrHead" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L0,6 L6,3 z" fill="#39FF14" opacity="0.7" />
            </marker>
          </defs>
          {/* CSV source */}
          <g>
            <rect x="10" y="80" width="70" height="42" rx="2" fill="#121212" stroke="#3f3f46" strokeWidth="1" />
            <text x="45" y="98" fill="#f4f4f5" fontSize="9" fontFamily="ui-monospace, monospace" textAnchor="middle" fontWeight="bold">DK CSV</text>
            <text x="45" y="112" fill="#71717a" fontSize="7" fontFamily="ui-monospace, monospace" textAnchor="middle">Entries.csv</text>
          </g>
          {/* Engine hub */}
          <g>
            <rect x="120" y="70" width="80" height="62" rx="2" fill="#0a1a0a" stroke="#39FF14" strokeWidth="1.5" />
            <text x="160" y="90" fill="#39FF14" fontSize="9" fontFamily="ui-monospace, monospace" textAnchor="middle" fontWeight="bold">WARPAINT</text>
            <text x="160" y="103" fill="#39FF14" fontSize="7" fontFamily="ui-monospace, monospace" textAnchor="middle" opacity="0.8">ROUTE ENGINE</text>
            <text x="160" y="120" fill="#a1a1aa" fontSize="6" fontFamily="ui-monospace, monospace" textAnchor="middle">Fingerprint × N</text>
          </g>
          {/* Arrow: CSV → engine */}
          <line x1="82" y1="101" x2="118" y2="101" stroke="#39FF14" strokeWidth="1.5" opacity="0.7" markerEnd="url(#arrHead)" />
          {/* Contest fan-out (3 slots) */}
          {[
            { y: 20, label: "MILLY", sub: "150 lineups" },
            { y: 78, label: "20-MAX", sub: "20 lineups" },
            { y: 136, label: "3-MAX", sub: "3 lineups" },
          ].map((c, i) => (
            <g key={i}>
              <rect x="230" y={c.y} width="80" height="42" rx="2" fill="#121212" stroke="#39FF14" strokeWidth="1" opacity="0.85" />
              <text x="270" y={c.y + 18} fill="#39FF14" fontSize="9" fontFamily="ui-monospace, monospace" textAnchor="middle" fontWeight="bold">{c.label}</text>
              <text x="270" y={c.y + 32} fill="#71717a" fontSize="7" fontFamily="ui-monospace, monospace" textAnchor="middle">{c.sub}</text>
              {/* Curved arrow from engine to this contest */}
              <path
                d={`M 200 101 Q 215 101, 228 ${c.y + 21}`}
                stroke="#39FF14"
                strokeWidth="1"
                fill="none"
                opacity="0.55"
                markerEnd="url(#arrHead)"
              />
            </g>
          ))}
          {/* "unique" tag under engine */}
          <text x="160" y="152" fill="#39FF14" fontSize="7" fontFamily="ui-monospace, monospace" textAnchor="middle" opacity="0.8">
            173 GUARANTEED-UNIQUE
          </text>
        </svg>
      </div>
    </div>
  );
}

function UploadPrompt({ fileRef, uploading, onUpload }) {
  return (
    <div className="mt-5 border-2 border-dashed border-zinc-800 hover:border-[#39FF14]/40 transition-colors bg-black/30 px-5 py-8 text-center">
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        onChange={onUpload}
        disabled={uploading}
        className="hidden"
        data-testid="entry-template-file-input"
      />
      <UploadSimple size={28} className="text-zinc-500 mx-auto mb-3" />
      <div className="text-sm text-zinc-400 mb-4 leading-relaxed">
        <span className="text-white font-semibold">Drop your DK Entries CSV here.</span> On DraftKings:
        Contest → Manage Entries → <span className="mono text-[#39FF14]">Export Entries</span>.
      </div>
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid="entry-template-upload-btn"
        className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-50 text-black font-bold uppercase tracking-widest text-xs py-3 px-5 transition-colors"
      >
        <FileArrowUp size={14} weight="bold" />
        {uploading ? "Parsing…" : "Choose CSV"}
      </button>
      <div className="text-[10px] text-zinc-600 mono mt-4 uppercase tracking-widest">
        Required columns: Entry ID · Contest Name · Contest ID · Entry Fee
      </div>
    </div>
  );
}

function TemplateSummary({ template, countOverrides, onClear }) {
  const totalFee = template.contests.reduce(
    (s, c) => s + (c.entry_fee || 0) * (c.entry_count || 0), 0
  );
  // Total lineups the user is asking WarPaint to generate — reflects any
  // per-contest count overrides so the "you're about to generate 340 unique
  // lineups" preview stays accurate.
  const totalLineups = template.contests.reduce((s, c) => {
    const o = countOverrides?.[c.contest_id];
    const n = (o === undefined || o === "") ? c.entry_count : parseInt(o, 10);
    return s + (Number.isFinite(n) && n >= 0 ? n : c.entry_count);
  }, 0);
  return (
    <div className="mt-5 flex items-center justify-between flex-wrap gap-3 border-t border-zinc-800 pt-4">
      <div className="flex items-center gap-4 flex-wrap">
        <Stat label="Reserved Slots" value={template.total_entries} accent="text-zinc-300" />
        <Stat label="Lineups to Generate" value={totalLineups} accent="text-[#39FF14]" />
        <Stat label="Contests" value={template.contests.length} accent="text-white" />
        <Stat label="Total Buy-in" value={`$${totalFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} accent="text-white" />
        <div className="text-[10px] mono text-zinc-500 uppercase tracking-widest">
          {template.filename || "entries.csv"}
        </div>
      </div>
      <button
        onClick={onClear}
        data-testid="entry-template-clear-btn"
        className="inline-flex items-center gap-1 text-zinc-500 hover:text-[#FF3B30] text-[10px] uppercase tracking-widest font-bold"
      >
        <Trash size={12} weight="bold" /> Clear
      </button>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-black/40 border border-zinc-900 px-3 py-2">
      <div className="overline text-[9px]">{label}</div>
      <div className={`mono font-black text-lg leading-none mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
}

function ContestTable({ contests, countOverrides, onOverrideChange, lastResult }) {
  const resultByContest = {};
  (lastResult?.batches || []).forEach((b) => { resultByContest[b.contest_id] = b; });

  return (
    <div className="mt-4 border border-zinc-800 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-black/60">
          <tr className="text-left text-zinc-400 uppercase tracking-widest text-[10px]">
            <th className="px-3 py-2">Contest</th>
            <th className="px-3 py-2">Entry Fee</th>
            <th className="px-3 py-2" title="Slots you have reserved on DK for this contest">Reserved</th>
            <th className="px-3 py-2" title="How many unique lineups WarPaint will generate for this contest. Blank = fill every reserved slot. Set lower to partial-fill (unused slots stay empty on DK).">
              # Lineups
            </th>
            <th className="px-3 py-2">Preset</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {contests.map((c) => {
            const r = resultByContest[c.contest_id];
            const overrideVal = countOverrides?.[c.contest_id];
            return (
              <tr key={c.contest_id} className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`entry-contest-row-${c.contest_id}`}>
                <td className="px-3 py-2 text-white font-medium max-w-[240px] truncate" title={c.contest_name}>
                  {c.contest_name}
                </td>
                <td className="px-3 py-2 mono text-zinc-400">
                  ${(c.entry_fee || 0).toFixed(2)}
                </td>
                <td className="px-3 py-2 mono text-zinc-300 font-bold">
                  {c.entry_count}
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min="0"
                    max="500"
                    placeholder={String(c.entry_count)}
                    value={overrideVal ?? ""}
                    onChange={(e) => onOverrideChange(c.contest_id, e.target.value)}
                    data-testid={`entry-contest-count-${c.contest_id}`}
                    className="w-16 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1 mono text-[#39FF14] text-[11px] font-bold text-center"
                  />
                </td>
                <td className="px-3 py-2">
                  <PresetBadge preset={c.preset} label={c.preset_label} />
                </td>
                <td className="px-3 py-2">
                  {r?.error ? (
                    <span className="text-[#FF3B30] mono text-[10px] flex items-center gap-1">
                      <WarningCircle size={10} weight="fill" /> {r.error}
                    </span>
                  ) : r ? (
                    <span className="text-[#39FF14] mono text-[10px] flex items-center gap-1">
                      <CheckCircle size={10} weight="fill" />
                      {r.generated} lineups · {r.avg_projection?.toFixed(1)} proj
                    </span>
                  ) : (
                    <span className="text-zinc-600 mono text-[10px]">Awaiting optimize</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PresetBadge({ preset, label }) {
  const colors = {
    milly_maker: "bg-[#39FF14] text-black",
    "20_max": "bg-[#007AFF] text-white",
    three_max: "bg-[#FF8A00] text-black",
    single_entry_150: "bg-zinc-700 text-white",
    flea_flicker: "bg-[#FFCC00] text-black",
    small_gpp: "bg-zinc-800 text-white",
    cash_50_50: "bg-zinc-900 text-zinc-300",
    head_to_head: "bg-zinc-900 text-zinc-300",
  };
  const c = colors[preset] || "bg-zinc-800 text-zinc-400";
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${c}`}>
      {label || preset}
    </span>
  );
}

function ActionRow({ onOptimize, optimizing, hasResult }) {
  return (
    <div className="mt-5 flex items-center gap-3 flex-wrap">
      <button
        onClick={onOptimize}
        disabled={optimizing}
        data-testid="entry-template-optimize-btn"
        className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-50 text-black font-black uppercase tracking-widest text-xs py-3 px-5 transition-colors"
      >
        <Target size={14} weight="fill" />
        {optimizing ? "Optimizing all contests…" : "Auto-Optimize All Contests"}
      </button>
      {hasResult && (
        <a
          href={api.entryTemplateExportUrl()}
          data-testid="entry-template-export-btn"
          className="inline-flex items-center gap-2 border-2 border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 font-black uppercase tracking-widest text-xs py-3 px-5 transition-colors"
        >
          <DownloadSimple size={14} weight="bold" />
          Download Merged CSV
        </a>
      )}
      <div className="text-[10px] mono text-zinc-500 uppercase tracking-widest">
        <Lightning size={10} weight="fill" className="inline mr-1 text-[#39FF14]" />
        One CSV, all contests · Entry IDs preserved
      </div>
    </div>
  );
}

function ResultsBanner({ result }) {
  const total = result.batches.reduce((s, b) => s + (b.generated || 0), 0);
  return (
    <div className="mt-4 bg-black/40 border border-[#39FF14]/40 px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle size={14} weight="fill" className="text-[#39FF14]" />
        <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] font-bold">
          {total} lineups generated across {result.contests_optimized} contests
        </div>
      </div>
      <div className="text-[11px] text-zinc-400 leading-relaxed">
        Ready to ship. Download the merged CSV and drop it on DraftKings' Bulk Upload page —
        each lineup lands in its correct contest slot automatically via the preserved Entry IDs.
      </div>
    </div>
  );
}
